import React from 'react'
import type { NextPage, GetServerSidePropsContext } from 'next'
import { useTranslations } from 'next-intl'
import { Box, useColorModeValue, Divider } from '@chakra-ui/react'
import { InfoFrame } from '@/components/InfoFrame'
import { PageHero } from '@/components/PageHero'
import { Highlights } from '@/components/Highlights'
import { VerticalSlideshow } from '@/components/VerticalSlideshow'
import { ContainerBase } from '@/components/containers/ContainerBase'
import { SectionWithCards } from '@/components/SectionWithCards'
import { TagIcon } from '@/components/icons/TagIcon'
import { PaymentIcon } from '@/components/icons/PaymentIcon'
import { PersonIconV2 } from '@/components/icons/PersonIconV2'
import { ShopNowCard } from '@/components/ShopNowCard'
import { SwapIcon } from '@/components/icons/SwapIcon'
import { HighlightItem } from '@/types/highlights.interface'
import { ISlide } from '@/types/strapi/commonComponents.interface'
import SeoHead from '@/components/seo-head'
import type { CardData } from '@/components/SectionWithCards'

const MarketplacePage: NextPage = () => {
  const t = useTranslations()
  const cardBg = useColorModeValue('white', 'black')
  const cardTitleColor = useColorModeValue('black', 'white')
  const cardDescriptionColor = useColorModeValue(
    'blackAlpha.800',
    'whiteAlpha.800'
  )
  const cardBorderColor = useColorModeValue('blackAlpha.500', 'whiteAlpha.500')
  const bg = useColorModeValue('blackAlpha.50', 'whiteAlpha.100')
  const dividerBg = useColorModeValue('blackAlpha.500', 'whiteAlpha.500')

  const items: HighlightItem[] = [
    {
      icon: PersonIconV2,
      title: t('common.kyc'),
      description: t('common.complete-kyc'),
    },
    {
      icon: TagIcon,
      title: t('common.list'),
      description: t('marketplace-landing.create-listing'),
    },
    {
      icon: SwapIcon,
      title: t('common.transfer'),
      description: t('marketplace-landing.ownership-transfer'),
    },
    {
      icon: PaymentIcon,
      title: t('common.payout'),
      description: t('marketplace-landing.transfer-complete'),
    },
  ]

  const flexibilitySlideshow: ISlide[] = [
    {
      id: t('marketplace-landing.effortless-exit'),
      title: t('marketplace-landing.effortless-exit'),
      description: t('marketplace-landing.easily-sell'),
      image: { url: '/img/set-listing-price.jpg' },
      imageMobile: { url: '/img/set-listing-price.jpg' },
      hasFrame: true,
    },
    {
      id: t('marketplace-landing.low-barrier-entry'),
      title: t('marketplace-landing.low-barrier-entry'),
      description: t('marketplace-landing.minimal-upfront-costs'),
      image: { url: '/img/available-units.jpg' },
      imageMobile: { url: '/img/available-units.jpg' },
      hasFrame: true,
    },
  ]

  const reliabilitySlides: ISlide[] = [
    {
      id: t('marketplace-landing.quality-assurance'),
      title: t('marketplace-landing.quality-assurance'),
      description: t('marketplace-landing.health-checks'),
      image: { url: '/img/quality-assured.jpg' },
      imageMobile: { url: '/img/quality-assured.jpg' },
    },
    {
      id: t('marketplace-landing.seven-day-warranty'),
      title: t('marketplace-landing.seven-day-warranty'),
      description: t('marketplace-landing.warranty-on-purchases'),
      image: { url: '/img/days-warranty.jpg' },
      imageMobile: { url: '/img/days-warranty.jpg' },
    },
  ]

  const liquiditySlides: ISlide[] = [
    {
      id: t('marketplace-landing.capitalize-volatility'),
      title: t('marketplace-landing.capitalize-volatility'),
      description: t('marketplace-landing.capitalize-bitcoin'),
      image: { url: '/img/marketplace-slide1.gif' },
      imageMobile: { url: '/img/marketplace-slide1.gif' },
    },
    {
      id: t('marketplace-landing.enhanced-liquidity'),
      title: t('marketplace-landing.enhanced-liquidity'),
      description: t('marketplace-landing.greater-liquidity'),
      image: { url: '/img/marketplace-slide2.gif' },
      imageMobile: { url: '/img/marketplace-slide2.gif' },
    },
  ]

  const fleetOptimization: CardData[] = [
    {
      title: t('marketplace-landing.fleet-optimization'),
      description: t('marketplace-landing.buy-sell-asics'),
      iconImage: { url: '/img/fleet.png' },
    },
    {
      title: t('marketplace-landing.maximize-returns'),
      description: t('marketplace-landing.improve-financial-health'),
      iconImage: { url: '/img/maximize-returns.png' },
      iconImageMobile: { url: '/img/maximize-returns-mobile.png' },
    },
  ]

  return (
    <>
      <SeoHead
        title={t('marketplace-landing.seo.title')}
        breadcrumbTitle={t('marketplace-landing.seo.breadcrumb')}
        description={t('marketplace-landing.seo.description')}
      />
      <PageHero
        title={t('marketplace-landing.buy-sell-miners')}
        subTitle={t('marketplace-landing.compass-mining-marketplace')}
        description={t('marketplace-landing.unlock-flexibility')}
        image="img/marketplace-hero.png"
        mobileImage="img/marketplace-hero.png"
        anchor="what-is-marketplace"
      />
      <Box id="what-is-marketplace">
        <InfoFrame
          title={t('marketplace-landing.what-is-marketplace')}
          description={t.rich('marketplace-landing.easy-buy-sell', {
            br: () => <br />,
          })}
        />
      </Box>
      <Highlights
        title={t('marketplace-landing.sell-anytime')}
        items={items}
        columns={4}
      />
      <ShopNowCard />
      <ContainerBase px={{ base: '32px', md: 4 }}>
        <Box py={{ base: '64px', md: '100px' }}>
          <VerticalSlideshow
            title={t('marketplace-landing.flexiblility-accessibility')}
            subtitle={t('marketplace-landing.getting-started')}
            slideshow={flexibilitySlideshow}
            reverseLayout
            alignStart
          />
        </Box>
        <Divider bg={dividerBg} opacity={0.5} />
        <Box py={{ base: '64px', md: '100px' }}>
          <VerticalSlideshow
            title={t('marketplace-landing.reability-trust')}
            subtitle={t('marketplace-landing.buy-sell-confidence')}
            slideshow={reliabilitySlides}
            alignStart
          />
        </Box>
      </ContainerBase>
      <Box bg={bg}>
        <ContainerBase
          px={{ base: '32px', md: 4 }}
          py={{ base: '42px', md: '100px' }}
        >
          <SectionWithCards
            title={t('marketplace-landing.optimize-fleet')}
            subtitle={t('marketplace-landing.easily-upgrade')}
            cards={fleetOptimization}
            cardImageOverlay={false}
            cardImageBg="gray.890"
            cardContentPy={8}
            cardBg={cardBg}
            cardTitleColor={cardTitleColor}
            cardDescriptionColor={cardDescriptionColor}
            cardBorderColor={cardBorderColor}
            cardImageHeight="252px"
            alignStart
            columns={2}
          />
        </ContainerBase>
      </Box>
      <ContainerBase
        px={{ base: '32px', md: 4 }}
        py={{ base: '64px', md: '100px' }}
      >
        <VerticalSlideshow
          title={t('marketplace-landing.strategic-trading')}
          subtitle={t('marketplace-landing.getting-started')}
          slideshow={liquiditySlides}
          reverseLayout
          alignStart
        />
      </ContainerBase>
    </>
  )
}

export async function getStaticProps({ locale }: GetServerSidePropsContext) {
  return {
    props: {
      messages: (await import(`../../../locales/${locale}.json`)).default,
    },
  }
}

export default MarketplacePage
